import { Locales } from '@griply/types';
import { useEffect, useRef } from 'react';
import { useCachedPushNotificationTokenQuery } from 'shared/hooks/queries/use-cached-push-notification-token-query';
import { useCreatePushTokenMutation } from 'shared/hooks/queries/use-create-push-token-mutation';
import { useFcmTokenDocQuery } from 'shared/hooks/queries/use-fcm-token-doc-query';
import { useUpdatePushTokenTimezoneMutation } from 'shared/hooks/queries/use-update-push-token-timezone-mutation';
import { getErrorReporter } from 'shared/services/error-tracking';
import { ID } from 'shared/types/id';

export const usePushTokenTimezoneSync = (
  uid: ID | undefined,
  locale: Locales,
): void => {
  const isSyncing = useRef(false);
  const { mutateAsync: updateTimezone } = useUpdatePushTokenTimezoneMutation();
  const { mutateAsync: createToken } = useCreatePushTokenMutation();

  const { data: token } = useCachedPushNotificationTokenQuery(uid);
  const { data: tokenDoc } = useFcmTokenDocQuery(token);

  const tokenRef = useRef(token);
  const tokenDocRef = useRef(tokenDoc);
  tokenRef.current = token;
  tokenDocRef.current = tokenDoc;

  useEffect(() => {
    if (!uid) {
      return;
    }

    const sync = async () => {
      if (isSyncing.current) {
        return;
      }
      isSyncing.current = true;

      try {
        const currentToken = tokenRef.current;
        if (!currentToken) {
          return;
        }

        const currentTimezone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;
        const doc = tokenDocRef.current;

        if (!doc) {
          await createToken({ userId: uid, token: currentToken, locale });
          return;
        }

        const storedTimezone = doc.timezone;
        if (storedTimezone === currentTimezone) {
          return;
        }

        await updateTimezone({
          token: currentToken,
          timezone: currentTimezone,
        });
      } catch (error) {
        getErrorReporter().captureException(error, {
          tags: { source: 'push-token-timezone-sync' },
        });
      } finally {
        isSyncing.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sync();
      }
    };

    const handleFocus = () => {
      sync();
    };

    sync();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [uid, locale, updateTimezone, createToken]);
};
